import { useMemo } from "react";
import UserAvatar from "@/components/ui/UserAvatar";

/** Couleurs standard des badges d'action (iter166 — modèle unique d'historique). */
export const HISTORY_COLORS = {
  green: "#37c99a",   // CRÉÉ, VALIDÉE
  amber: "#E0A82E",   // MODIFIÉ
  red: "#E24B4A",     // RÉOUVERTE, fichier retiré, SUPPRIMÉ
  blue: "#3568B0",    // CLÔTURÉ
  grey: "#8a8a8f",
  violet: "#8B5CF6",
  orange: "#F97316",
};

const dayKey = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }).toUpperCase();
};
const hhmm = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
};

export function HistoryBadge({ label, color = HISTORY_COLORS.grey, testId }) {
  return (
    <span className="inline-flex items-center font-bold uppercase whitespace-nowrap"
      style={{ fontSize: 10, padding: "1px 7px", borderRadius: 4, background: "transparent", border: `1px solid ${color}80`, color, letterSpacing: ".04em", lineHeight: 1.3 }}
      data-testid={testId}>
      {label}
    </span>
  );
}

/**
 * Modèle standard d'historique : tri ascendant (CRÉÉ en tête), groupes par jour,
 * grille Heure · Avatar · Badge · Détail.
 * events: [{ id, at, user, userName, badge, color, detail (node|string), sub (node|string) }]
 */
export default function HistoryTimeline({ events = [], emptyMessage = "Aucun événement.", testId = "history-timeline", maxHeight, showName = false, newestFirst = false }) {
  const groups = useMemo(() => {
    const sorted = [...events].filter(Boolean).sort((a, b) => (newestFirst ? new Date(b.at) - new Date(a.at) : new Date(a.at) - new Date(b.at)));
    const out = [];
    for (const e of sorted) {
      const k = dayKey(e.at);
      if (!out.length || out[out.length - 1].key !== k) out.push({ key: k, items: [] });
      out[out.length - 1].items.push(e);
    }
    return out;
  }, [events, newestFirst]);

  if (!groups.length) {
    return <div className="text-[12px] italic text-center py-6" style={{ color: "#71717c" }} data-testid={`${testId}-empty`}>{emptyMessage}</div>;
  }
  return (
    <div data-testid={testId} style={maxHeight ? { maxHeight, overflowY: "auto" } : undefined}>
      {groups.map((g) => (
        <div key={g.key} data-testid={`${testId}-day`}>
          <div className="px-4 pt-4 pb-1.5 font-semibold" style={{ fontSize: 11, color: "#8a8a8f", letterSpacing: ".08em" }}>{g.key}</div>
          {g.items.map((e, i) => {
            const u = e.user || (e.userName ? { first_name: e.userName.split(" ")[0] || "", last_name: e.userName.split(" ").slice(1).join(" ") } : null);
            return (
              <div key={e.id || i} className="grid items-start px-4 py-2"
                style={{ gridTemplateColumns: showName ? "44px 170px 118px minmax(0,1fr)" : "44px 30px 118px minmax(0,1fr)", columnGap: 10, borderBottom: "1px solid #17171b" }}
                data-testid={e.id ? `history-item-${e.id}` : undefined}>
                <span className="tabular-nums pt-0.5" style={{ fontSize: 11, color: "#a9a9b2" }}>{hhmm(e.at)}</span>
                <span className="flex items-center min-w-0" style={{ gap: 6 }}>{u ? <UserAvatar user={u} size={22} /> : <span style={{ color: "#71717c", fontSize: 11 }}>—</span>}{showName && <span className="truncate" style={{ fontSize: 11.5, color: "#c9c9d1" }} data-testid="history-user-name">{u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || e.userName : e.userName || "Système"}</span>}</span>
                <span className="pt-0.5"><HistoryBadge label={e.badge || "ÉVÉNEMENT"} color={e.color} testId={e.id ? `history-badge-${e.id}` : undefined} /></span>
                <div className="min-w-0">
                  <div style={{ fontSize: 12.5, color: "#ececf0" }} className="break-words">{e.detail}</div>
                  {e.sub ? <div className="mt-0.5 break-words" style={{ fontSize: 11, color: "#71717c" }}>{e.sub}</div> : null}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
